import {
  type DisplayTranscript,
  type TypedTranscript,
  captureTypedTranscript,
} from "@/lib/typedTranscriptDisplay";
import type { Room } from "livekit-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const TYPED_TRANSCRIPT_SPLIT_GRACE_MS = 1200;

function extendSnapshot(message: TypedTranscript, voice: DisplayTranscript[]): TypedTranscript {
  return {
    ...message,
    assistantSnapshot: Object.fromEntries(
      Object.entries(message.assistantSnapshot).map(([id, prefix]) => {
        const current = voice.find((item) => item.id === id)?.text;
        return [id, current?.startsWith(prefix) ? current : prefix];
      })
    ),
  };
}

/** Send immediately; briefly let the existing tutor prefix grow before freezing it. */
export function useTypedTranscriptMessages(room: Room | null, voice: DisplayTranscript[]) {
  const currentRoom = useRef(room);
  currentRoom.current = room;
  const latestVoice = useRef(voice);
  latestVoice.current = voice;
  const graceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (graceTimer.current !== null) clearTimeout(graceTimer.current);
    },
    [room]
  );
  const [state, setState] = useState<{
    room: Room | null;
    messages: TypedTranscript[];
    graceId: string | null;
  }>({
    room,
    messages: [],
    graceId: null,
  });
  const sendTypedMessage = useCallback(
    async (text: string) => {
      if (!room) return;
      const timestamp = Date.now();
      const message = captureTypedTranscript(
        text,
        voice,
        timestamp,
        `typed_user_${timestamp}_${crypto.randomUUID()}`
      );
      if (graceTimer.current !== null) clearTimeout(graceTimer.current);
      setState((previous) => ({
        room,
        messages: [
          ...(previous.room === room
            ? previous.messages.map((item) =>
                item.id === previous.graceId ? extendSnapshot(item, voice) : item
              )
            : []),
          message,
        ],
        graceId: message.id,
      }));
      graceTimer.current = setTimeout(() => {
        graceTimer.current = null;
        if (currentRoom.current !== room) return;
        setState((previous) =>
          previous.room !== room || previous.graceId !== message.id
            ? previous
            : {
                ...previous,
                graceId: null,
                messages: previous.messages.map((item) =>
                  item.id === message.id ? extendSnapshot(item, latestVoice.current) : item
                ),
              }
        );
      }, TYPED_TRANSCRIPT_SPLIT_GRACE_MS);
      try {
        await room.localParticipant.sendText(text, { topic: "lk.chat" });
        if (currentRoom.current !== room) return;
        setState((previous) =>
          previous.room !== room
            ? previous
            : {
                ...previous,
                messages: previous.messages.map((item) =>
                  item.id === message.id ? { ...item, pending: false } : item
                ),
              }
        );
      } catch (error) {
        if (currentRoom.current === room) {
          setState((previous) =>
            previous.room !== room
              ? previous
              : {
                  ...previous,
                  messages: previous.messages.filter((item) => item.id !== message.id),
                  graceId: previous.graceId === message.id ? null : previous.graceId,
                }
          );
        }
        throw error;
      }
    },
    [room, voice]
  );
  const typedMessages = useMemo(
    () =>
      state.room === room
        ? state.messages.map((item) =>
            item.id === state.graceId ? extendSnapshot(item, voice) : item
          )
        : [],
    [state, room, voice]
  );
  return { typedMessages, sendTypedMessage };
}
